import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import clsx from 'clsx';
import { AVideoBrightcoveInternalProps, AVideoAspectRatio, BrightcovePlayerSuccess } from '../AVideo.types';
import { useVideoPlayState } from '../hooks/useVideoPlayState';
import * as Styled from '../AVideo.styles';
import { DEFAULT_VIDEO_ARIA_LABEL } from '../../../utils/i18nConstants';

export const COMPONENT_NAME = 'a-web-ui-kit-AVideo';
export const TEST_ID = 'web-ui-kit-AVideo-brightcove';

export const AVideoBrightcove: React.FC<AVideoBrightcoveInternalProps> = ({
  videoId,
  videoAccountId,
  playerId = 'default',
  aspectRatio = AVideoAspectRatio.Ratio16x9,
  isPlaying,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  onAutoplayBlocked,
  onEnded,
  onPlayerReady,
  onPlayerError,
  className,
  'aria-label': ariaLabel = DEFAULT_VIDEO_ARIA_LABEL,
  ...rest
}) => {
  const { playerRef, applyPendingPlayState } = useVideoPlayState({
    isPlaying,
    onAutoplayBlocked,
  });

  // Capture the intended startup behavior on first render. The parent may drive
  // playback via `isPlaying` (async, e.g. gated on a media query) so the initial
  // `autoPlay` prop alone is not a reliable signal. A video explicitly paused at
  // mount (`isPlaying === false`, e.g. a non-active carousel slide) must not
  // native-autoplay.
  const explicitlyPaused = isPlaying === false;
  const wantsToPlay = !explicitlyPaused && (autoPlay || isPlaying === true);

  // Stabilize options passed to ReactPlayerLoader so prop changes don't cause
  // a full player dispose/recreate. Play/pause is handled imperatively via useVideoPlayState.
  //
  // For muted videos that want to play we let Brightcove's native autoplay engine
  // drive startup (`autoplay: 'muted'`). Browsers reliably allow muted autoplay,
  // and native autoplay starts as soon as the media can play — avoiding the
  // race (observed on Windows) where a programmatic play() before the player is
  // ready silently no-ops. Non-active slides stay paused and are resumed
  // imperatively when they become active.
  // `wantsToPlay` and `muted` are intentionally frozen at mount — the Brightcove
  // player reads `options` once during initialisation; updating it causes a full
  // dispose/recreate. Subsequent play/pause is driven imperatively via
  // `useVideoPlayState` (see `applyPendingPlayState`). Suppress the exhaustive-deps
  // lint rule: this empty dep array is load-bearing, not an oversight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOptions = useMemo(
    () => ({
      responsive: true,
      fluid: true,
      autoplay: (wantsToPlay ? (muted ? 'muted' : true) : false) as boolean,
      muted,
      loop,
    }),
    []
  );

  // Stable refs so listeners registered once in handleSuccess always read latest values
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked);
  onAutoplayBlockedRef.current = onAutoplayBlocked;

  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const onPlayerReadyRef = useRef(onPlayerReady);
  onPlayerReadyRef.current = onPlayerReady;

  // Handler refs stored so the unmount cleanup can call player.off with the
  // exact function references that were passed to player.on.
  const autoplayFailureHandlerRef = useRef<(() => void) | null>(null);
  const endedHandlerRef = useRef<(() => void) | null>(null);

  const handleSuccess = useCallback(
    (success: BrightcovePlayerSuccess) => {
      const player = success?.ref;
      if (!player) return;

      // applyPendingPlayState also sets playerRef.current
      applyPendingPlayState(player);

      if (typeof player.controls === 'function') {
        player.controls(controlsRef.current);
      }

      // @brightcove Video.js fires 'autoplay-failure' when the browser blocks autoplay
      if (typeof player.on === 'function') {
        // Defensive: remove previous listeners in case handleSuccess fires more than once
        if (typeof player.off === 'function') {
          if (autoplayFailureHandlerRef.current) {
            player.off('autoplay-failure', autoplayFailureHandlerRef.current);
          }
          if (endedHandlerRef.current) {
            player.off('ended', endedHandlerRef.current);
          }
        }

        const handleAutoplayFailure = () => {
          onAutoplayBlockedRef.current?.();
        };
        const handleEnded = () => {
          onEndedRef.current?.();
        };

        autoplayFailureHandlerRef.current = handleAutoplayFailure;
        endedHandlerRef.current = handleEnded;

        player.on('autoplay-failure', handleAutoplayFailure);
        player.on('ended', handleEnded);
      }

      onPlayerReadyRef.current?.(player);
    },
    [applyPendingPlayState]
  );

  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      const player = playerRef.current;
      if (player && typeof player.off === 'function') {
        if (autoplayFailureHandlerRef.current) {
          player.off('autoplay-failure', autoplayFailureHandlerRef.current);
        }
        if (endedHandlerRef.current) {
          player.off('ended', endedHandlerRef.current);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup reads from stable refs on unmount only
  }, []);

  if (!videoId?.trim() || !videoAccountId?.trim()) {
    return null;
  }

  const videoIdValue = videoId.trim();
  const accountIdValue = videoAccountId.trim();
  const playerIdValue = playerId || 'default';
  const sanitizedVideoId = videoIdValue.replace(/[^a-zA-Z0-9_-]/g, '');
  const videoElementId = `brightcove-video-${sanitizedVideoId}`;

  return (
    <Styled.BrightcoveWrapper
      $aspectRatio={aspectRatio}
      data-aspect-ratio={aspectRatio}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(className)}
      {...rest}
    >
      <ReactPlayerLoader
        id={videoElementId}
        accountId={accountIdValue}
        videoId={videoIdValue}
        playerId={playerIdValue}
        onSuccess={handleSuccess}
        onFailure={onPlayerError}
        options={stableOptions}
        manualReloadFromPropChanges
        aria-label={ariaLabel}
      />
    </Styled.BrightcoveWrapper>
  );
};
